import { ReactNode, useState, isValidElement } from 'react';
import { useTranslation } from 'react-i18next';
import Button from './Button';
import styles from './Alert.module.scss';

type AlertType = 'success' | 'error' | 'warning' | 'info';
export type { AlertType };

/**
 * HAT-1239 anatomy:
 *   [icon]  [title]                                   [X]
 *           [body]
 *           [quiet CTA link]
 *
 * `inline` keeps all three on one line - [icon] [title, body, link] [X] - for a banner whose
 * whole message is a sentence. Same colours, same parts, one row.
 *
 * Backward compatible: callers that only pass `message` still work — the message
 * renders in the body slot as a single line.
 */
interface AlertAction {
  label: string;
  onClick: () => void;
}

interface AlertProps {
  type?: AlertType;
  title?: string;
  /** Body text. Aliased by `message` for legacy callers. */
  body?: string;
  /** Legacy shorthand kept for existing callers that only render a single line. */
  message?: string;
  /**
   * Quiet CTA rendered below the body. Either a `{ label, onClick }` pair or a
   * ReactNode when the caller needs full control (e.g. a link or an inline form).
   */
  action?: AlertAction | ReactNode;
  /** Legacy: kept for the two remaining callers that render a confirm button. */
  onConfirm?: () => void;
  onClose?: () => void;
  /** Set to false to hide the dismiss X. Defaults to true. */
  dismissible?: boolean;
  /**
   * Title, body and CTA on one line instead of stacked. For a short message that reads as a
   * single sentence, where the stacked anatomy leaves a tall box around three short lines.
   */
  inline?: boolean;
  className?: string;
}

/* Glyphs follow the stroke style of the icons in assets/icons (24 grid, 1.5 stroke,
   round caps) but draw with currentColor so each one takes its alert's tone. */
const icons: Record<AlertType, ReactNode> = {
  error: (
    <>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path d="M12 7.5V13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="12" cy="16.25" r="1" fill="currentColor" />
    </>
  ),
  warning: (
    <>
      <path
        d="M20.39 21.1H4.2c-1.55 0-2.52-1.68-1.75-3.02l8.1-14.08a2 2 0 0 1 3.49 0l8.1 14.08c.77 1.34-.2 3.02-1.75 3.02Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path d="M12.29 9.5V13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="12.29" cy="17.1" r="1" fill="currentColor" />
    </>
  ),
  success: (
    <>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8.25 12.35 10.9 15l5-5.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="12" cy="8" r="1" fill="currentColor" />
      <path d="M12 11.25V16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </>
  ),
};

function isActionObject(value: unknown): value is AlertAction {
  return typeof value === 'object'
    && value !== null
    && !isValidElement(value)
    && typeof (value as AlertAction).label === 'string'
    && typeof (value as AlertAction).onClick === 'function';
}

const Alert: React.FC<AlertProps> = ({
  type = 'info',
  title,
  body,
  message,
  action,
  onConfirm,
  onClose,
  dismissible = true,
  inline = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const handleClose = () => {
    setDismissed(true);
    onClose?.();
  };

  const bodyText = body ?? message;
  const hasTitle = Boolean(title);
  const hasBody = Boolean(bodyText);

  const renderedAction = (() => {
    if (!action) return null;
    if (isActionObject(action)) {
      return (
        <button type="button" className={styles.actionLink} onClick={action.onClick}>
          {action.label}
        </button>
      );
    }
    return action;
  })();

  return (
    <div
      className={`${styles.alert} ${styles[type]} ${inline ? styles.inline : ''} ${className}`.trim()}
      role={type === 'error' || type === 'warning' ? 'alert' : 'status'}
    >
      <span className={styles.accent} aria-hidden="true" />

      <svg className={styles.icon} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        {icons[type]}
      </svg>

      <div className={styles.content}>
        {hasTitle && <p className={`${styles.title} body-md-semibold`}>{title}</p>}
        {hasBody && (
          <p
            className={`${styles.body} ${hasTitle ? 'body-sm-regular' : 'body-md-regular'}`}
          >
            {bodyText}
          </p>
        )}
        {renderedAction && <div className={styles.actionRow}>{renderedAction}</div>}
      </div>

      {(onConfirm || dismissible) && (
        <div className={styles.trailing}>
          {onConfirm && (
            <Button variant="outline" onClick={onConfirm}>
              {t('confirm')}
            </Button>
          )}
          {dismissible && (
            <button
              type="button"
              className={styles.closeButton}
              onClick={handleClose}
              aria-label={t('close')}
            >
              <svg className={styles.closeIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
                <path d="M5.5 18.5 18.5 5.5M18.5 18.5 5.5 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Alert;
